import { useGlobalContext } from '../GlobalContextProvider';
import { useCallback, useEffect } from 'react';
import { AuthenticationActionType, SpocState } from './authenticationReducer';
import Cookies from 'universal-cookie';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLocation } from 'react-router';
import { browserClient } from '../../react-query';
import { useSpocs } from '../../api/spocs/useSpocs';
import { SPOC_DATA } from '../../../useKeycloakAndInit';
import * as Sentry from '@sentry/react';

export const SPOC_COOKIE_NAME = 'spoc';

// FIXME It seems this file has some separation of concerns issues. It is used for some initialization in index.js, we should have a look at this.

// let cookie: Cookies | null = null; // FIXME should be initialized properly

export const cookie = new Cookies();

function saveSpocCookie(spoc: SpocState) {
    cookie.set(SPOC_COOKIE_NAME, { ...spoc, sites: [] }, { path: '/' });
}

export function useAuthentication(authenticated: boolean = true) {
    const { state, dispatch } = useGlobalContext();
    const navigate = useNavigate();
    const { data: spocs } = useSpocs(authenticated);

    const setSpoc = useCallback(
        (spoc: SpocState) => {
            saveSpocCookie(spoc);
            dispatch({ type: AuthenticationActionType.SET_SPOC, payload: spoc });
            SPOC_DATA.spocId = spoc.id;
            Sentry.setTag('spocId', spoc.id);
            browserClient.clear();
        },
        [dispatch]
    );

    const setAuthenticatedSpocById = useCallback(
        (newSpocId: string) => {
            const desiredSpoc = findSpoc(spocs ?? [], newSpocId);

            if (desiredSpoc) {
                setSpoc(desiredSpoc);
            } else {
                navigate('/', { replace: true });
            }
        },
        [spocs, navigate, setSpoc]
    );

    return {
        setAuthenticatedSpocById,
        setSpoc,
        spoc: state.authentication.spoc
    };
}

export function useSwitchSpoc() {
    const { setSpoc } = useAuthentication();
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { data: spocs } = useSpocs(true);

    return useCallback(
        (spocId: string) => {
            const desiredSpoc = findSpoc(spocs ?? [], spocId);
            if (!desiredSpoc) {
                // the user is not allowed to switch to the desired spoc -> redirect to the root page
                navigate('/', { replace: true });
                return;
            }
            saveSpocCookie(desiredSpoc);

            const spocIdParam = searchParams.get('spocId');
            if (spocIdParam && spocIdParam !== spocId) {
                searchParams.set('spocId', spocId);
            }

            setSpoc(desiredSpoc);
            navigate({ pathname, search: searchParams.toString() });
        },
        [navigate, pathname, searchParams, setSpoc, spocs]
    );
}

function findSpoc(spocs: ReadonlyArray<SpocState>, spocId: string) {
    return spocs.flatMap(partner => [partner, ...(partner.sites ?? [])]).find(spoc => spoc.id === spocId);
}

export function useAutomaticSpocSwitch() {
    const switchSpoc = useSwitchSpoc();

    const { spoc: currentSpoc } = useAuthentication();

    const [searchParams] = useSearchParams();
    const spocIdParam = searchParams.get('spocId');

    useEffect(() => {
        if (spocIdParam && spocIdParam !== currentSpoc?.id) {
            switchSpoc(spocIdParam);
        }
    }, [currentSpoc?.id, spocIdParam, switchSpoc]);
}
